
import React, { ChangeEvent, useState, useEffect, useRef } from "react";
import { Input, Button, Popup, InputOnChangeData, Label, Ref } from "semantic-ui-react";
import { useTranslation } from "react-i18next";
import format from "date-fns/format";

import { ErrorBoundary, useOnClickOutside } from "../../../sg-core";
import { DATE_FORMAT } from "../../constants/formats";
import Validator, { ERangeError } from "./DateTimePicker.validation"
import Calendar, { CalendarProps } from "react-calendar";
import { catchErrors, PickerUtils } from "./PickerUtils";

export interface IDateInput {
    minDate?: Date;
    maxDate?: Date;
    format?: string;
    errorMessage?: string;
    lang?: "fi" | "en" | string;

    value?: string;
    onChange: (date: string | undefined) => void;
}


export const DateInput: React.FC<IDateInput> = ({
    format: dateFormat = DATE_FORMAT,
    value,
    onChange,
    errorMessage,
    lang = "fi",
    ...props
}) => {
    const { t } = useTranslation()

    const minDate = props.minDate;
    const maxDate = props.maxDate;

    const inputRef = useRef<HTMLInputElement>(null);

    const [inputError, setInputError] = useState<string | undefined>();

    const [isOpen, setOpen] = useState(false);

    useEffect(() => {
        const inputElement = inputRef.current?.getElementsByTagName("INPUT")[0] as HTMLInputElement | undefined;
        if (inputElement && typeof value !== undefined && (inputElement.value || "") !== value) {
            inputElement.value = value || "";
        };

        if (!value && inputError) {
            setInputError(undefined);
        };

    }, [value])


    const updateInput = (inputValue?: string, strict = false) => {
        if (inputError) {
            setInputError(undefined)
        };

        if (!inputValue) {
            return onChange(undefined);
        };

        const inputTextSeemsCompleteDate = Validator.Date.validateInput(inputValue, strict)
        if (!inputTextSeemsCompleteDate) {
            return
        };

        const rangeError = Validator.Date.validateRange(inputValue, { minDate, maxDate });

        if (rangeError) {
            const errorMessage = Validator.Date.getRangeErrorMessage(rangeError, { minDate, maxDate }, t);
            setInputError(errorMessage);


            if (rangeError === ERangeError.InvalidDate) {
                onChange(undefined);
            }
            onChange(inputValue);
            return
        }

        onChange(inputValue);
    }


    const _onCalendarChange: CalendarProps["onChange"] = (value: Date) => {
        updateInput(format(value, DATE_FORMAT));
        setOpen(false);
    }

    const _onInputChange = (event: ChangeEvent<HTMLInputElement>, data: InputOnChangeData) => {
        inputError && setInputError(undefined);
        const value = event.target.value;
        updateInput(value, true);
    };


    const _onOutsideClick = () => {
        const inputElement = inputRef.current?.getElementsByTagName("INPUT")[0] as HTMLInputElement | undefined;
        if (inputElement && inputElement === document.activeElement) {
            inputError && setInputError(undefined);
            updateInput(inputElement?.value);
        }
    };

    useOnClickOutside(inputRef, _onOutsideClick);

    return (
        <div className="DateInput_wrapper">
            <Popup
                on="click"
                open={isOpen}
                position="bottom right"
                onClose={() => setOpen(false)}
                style={{ padding: 0, zIndex: 9999999 }}
                trigger={(
                    <Ref innerRef={inputRef}>
                        <Input
                            placeholder="pp.kk.vvvv"
                            onChange={_onInputChange}
                            //   value={value}
                            error={Boolean(inputError)}
                            label={
                                <Button
                                    ref={null}
                                    type="button"
                                    icon="calendar"
                                    onClick={() => setOpen(open => !open)}
                                />
                            }
                            labelPosition="right"
                        />
                    </Ref>
                )}
            >
                <ErrorBoundary fallback={<div> Error:  failed to display date picker </div>}>
                    <Calendar
                        minDate={minDate ? minDate : undefined}
                        maxDate={maxDate ? maxDate : undefined}
                        onChange={_onCalendarChange}
                        value={catchErrors(PickerUtils.inputTextToDate, [value, dateFormat], undefined)}
                    />
                </ErrorBoundary>
            </Popup>

            {(inputError || errorMessage) && <Label pointing negative prompt> {inputError || errorMessage}</Label>}
        </div>
    )
};